import { PALETTE, GLOW } from "../design-tools/tokens";

/* ─────────────────────────────────────────────
   LEGAL PAGE SHARED LAYOUT BITS
   (mirrors PrivacyPolicy.jsx — kept duplicated rather than
   factored out, to keep each legal page a single self-contained file)
───────────────────────────────────────────── */
function LegalNav() {
  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "22px 56px",
        background: "rgba(13,15,20,0.92)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: `1px solid ${PALETTE.border}`,
      }}>
      <a
        href="/"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          textDecoration: "none",
        }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 500,
            fontSize: "15px",
            letterSpacing: "0.06em",
            color: PALETTE.white,
            lineHeight: 1,
          }}>
          Digital Billions Group
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "8px",
            letterSpacing: "0.35em",
            textTransform: "uppercase",
            color: PALETTE.teal500,
          }}>
          Global Digital Innovation
        </span>
      </a>
      <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
        <a href="/terms" className="nav-link">
          Terms
        </a>
        <a href="/privacy" className="nav-link">
          Privacy
        </a>
        <a href="/#contact" className="nav-link">
          Contact
        </a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <div
      style={{
        borderTop: `1px solid ${PALETTE.border}`,
        padding: "24px 56px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
        background: "linear-gradient(180deg, transparent, rgba(13,15,20,0.6))",
      }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "9px",
          letterSpacing: "0.3em",
          textTransform: "uppercase",
          color: PALETTE.teal300,
        }}>
        ◈ DIGITAL BILLIONS GROUP &nbsp; ◌ &nbsp; MMXXII—MMXXVI
      </span>
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <a href="/" className="nav-link footer-link">
          Home
        </a>
        <a href="/privacy" className="nav-link footer-link">
          Privacy
        </a>
        <a
          href="mailto:hello@digitalbillionsgroup.com"
          className="nav-link footer-link">
          Contact
        </a>
      </div>
    </div>
  );
}

function Article({ number, title, children }) {
  return (
    <section style={{ marginBottom: 48 }}>
      <h2
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 400,
          fontSize: "clamp(20px, 2.4vw, 28px)",
          lineHeight: 1.2,
          color: PALETTE.white,
          marginBottom: 18,
          display: "flex",
          gap: 14,
          alignItems: "baseline",
        }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "13px",
            letterSpacing: "0.1em",
            color: PALETTE.teal500,
          }}>
          {number}
        </span>
        {title}
      </h2>
      <div
        style={{
          fontFamily: "var(--font-body)",
          fontWeight: 300,
          fontSize: "15.5px",
          lineHeight: 1.85,
          color: PALETTE.whiteMid,
        }}>
        {children}
      </div>
    </section>
  );
}

function Placeholder({ children }) {
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "0.92em",
        color: PALETTE.teal400,
        background: "rgba(127,161,194,0.1)",
        border: `1px dashed ${PALETTE.borderBright}`,
        padding: "1px 7px",
        borderRadius: 2,
      }}
      title="Placeholder — a business/legal decision, not filled in automatically. Confirm before publishing.">
      {children}
    </span>
  );
}

function Callout({ label, children }) {
  return (
    <div
      style={{
        border: `1px solid ${PALETTE.borderBright}`,
        background: "rgba(127,161,194,0.05)",
        padding: "18px 20px",
        marginBottom: 20,
      }}>
      <p
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "10px",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: PALETTE.teal400,
          marginBottom: 10,
        }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function TwoColTable({ headLeft, headRight, rows }) {
  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, marginBottom: 8 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          padding: "10px 16px",
          background: "rgba(127,161,194,0.06)",
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: PALETTE.teal400,
          fontFamily: "var(--font-mono)",
        }}>
        <span>{headLeft}</span>
        <span>{headRight}</span>
      </div>
      {rows.map(([a, b], i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            padding: "12px 16px",
            borderTop: `1px solid ${PALETTE.border}`,
            fontSize: "13.5px",
            color: PALETTE.whiteMid,
          }}>
          <span>{a}</span>
          <span>{b}</span>
        </div>
      ))}
    </div>
  );
}

function PrecedenceTable() {
  const rows = [
    [
      "1",
      "Master Services Agreement (MSA)",
      "Negotiated master contract for a specific Client.",
    ],
    [
      "2",
      "Statement of Work (SOW) / Order / License Schedule",
      "Engagement-specific scope, deliverables, fees, and rights.",
    ],
    [
      "3",
      "Business Associate Agreement (BAA) / Data Processing Addendum (DPA)",
      "Governs regulated or personal data (controls over these Terms for its subject matter).",
    ],
    [
      "4",
      "These Platform Terms of Service",
      "Default umbrella terms where no signed agreement covers the point.",
    ],
    [
      "5",
      "Privacy Policy",
      "Data-handling practices, incorporated by reference.",
    ],
  ];
  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, marginBottom: 8 }}>
      {rows.map(([num, doc, role], i) => (
        <div
          key={num}
          style={{
            display: "grid",
            gridTemplateColumns: "32px 1fr 1.3fr",
            gap: 16,
            padding: "12px 16px",
            borderTop: i === 0 ? "none" : `1px solid ${PALETTE.border}`,
            fontSize: "13.5px",
          }}>
          <span
            style={{ fontFamily: "var(--font-mono)", color: PALETTE.teal500 }}>
            {num}
          </span>
          <span style={{ color: PALETTE.white, fontWeight: 500 }}>{doc}</span>
          <span style={{ color: PALETTE.whiteMid }}>{role}</span>
        </div>
      ))}
    </div>
  );
}

function DefinitionsTable() {
  const rows = [
    [
      "Platform",
      "DBG's proprietary software framework, source code, architecture, APIs, dashboards, tooling, and all reusable systems and improvements, in all versions.",
    ],
    [
      "Managed Service",
      "Any Service for which DBG hosts, operates, monitors, or maintains software or infrastructure on a recurring basis.",
    ],
    [
      "Subscription",
      "A recurring plan (e.g., monthly or annual) granting access to the Platform, a Managed Service, or support, billed on a repeating cycle.",
    ],
    [
      "Client Content",
      "Data, records, files, branding, and materials you provide or generate through the Services, including any Protected Health Information where applicable.",
    ],
    [
      "Order",
      "Any SOW, proposal, License Schedule, online checkout, or written confirmation describing the Services and fees.",
    ],
    [
      "PHI",
      "Protected Health Information as defined under HIPAA and its implementing regulations.",
    ],
    [
      "AI Output",
      "Any text, code, recommendation, prediction, image, summary, or other content generated, in whole or in part, by an artificial intelligence or automated system used within the Services.",
    ],
    [
      "Open Source Component",
      "Any third-party library, framework, package, or dependency made available under an open-source or other third-party license and incorporated into or used alongside the Platform or a deliverable.",
    ],
  ];
  return (
    <div style={{ border: `1px solid ${PALETTE.border}`, marginBottom: 8 }}>
      {rows.map(([term, def], i) => (
        <div
          key={term}
          style={{
            display: "grid",
            gridTemplateColumns: "180px 1fr",
            gap: 16,
            padding: "12px 16px",
            borderTop: i === 0 ? "none" : `1px solid ${PALETTE.border}`,
            fontSize: "13.5px",
          }}>
          <span style={{ color: PALETTE.white, fontWeight: 500 }}>{term}</span>
          <span style={{ color: PALETTE.whiteMid }}>{def}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   PLATFORM TERMS OF SERVICE
───────────────────────────────────────────── */
export default function TermsOfService() {
  return (
    <div style={{ background: PALETTE.ink, minHeight: "100vh" }}>
      <LegalNav />

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "160px 24px 100px",
        }}>
        <p
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: "0.25em",
            textTransform: "uppercase",
            color: PALETTE.teal400,
            marginBottom: 24,
          }}>
          Platform & Services
        </p>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 300,
            fontSize: "clamp(34px, 5.5vw, 58px)",
            lineHeight: 1.04,
            letterSpacing: "-0.02em",
            color: PALETTE.white,
            marginBottom: 28,
            textShadow: GLOW.text,
          }}>
          Platform Terms of Service
        </h1>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontWeight: 300,
            fontSize: "17px",
            lineHeight: 1.8,
            color: PALETTE.whiteMid,
            marginBottom: 32,
          }}>
          The master terms governing access to Digital Billions Group services,
          platforms, and managed subscriptions.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            borderTop: `1px solid ${PALETTE.border}`,
            borderBottom: `1px solid ${PALETTE.border}`,
            padding: "16px 0",
            marginBottom: 48,
            fontFamily: "var(--font-mono)",
            fontSize: "11px",
            letterSpacing: "0.08em",
            color: PALETTE.teal300,
          }}>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>LAST UPDATED</strong>{" "}
            June 24, 2026
          </span>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>PROVIDER</strong> Digital
            Billions Group LLC
          </span>
          <span>
            <strong style={{ color: PALETTE.teal100 }}>APPLIES TO</strong> All
            Clients, Users &amp; Visitors
          </span>
        </div>

        <p
          style={{
            fontFamily: "var(--font-body)",
            fontStyle: "italic",
            fontSize: "15px",
            lineHeight: 1.8,
            color: PALETTE.whiteSoft,
            marginBottom: 56,
          }}>
          These Terms are published on the Digital Billions Group website and
          apply to every engagement unless a signed Master Services Agreement
          expressly provides otherwise.
        </p>

        <Article
          number="1"
          title="Acceptance & Relationship to Other Agreements">
          <p style={{ marginBottom: 20 }}>
            These Platform Terms of Service (the "Terms") govern your access to
            and use of the websites, applications, platforms, dashboards, and
            professional and managed services (collectively, the "Services")
            provided by Digital Billions Group LLC ("DBG," "we," "us," or
            "Company"). By accessing the Services, creating an account, signing
            a proposal or order, paying an invoice, or otherwise engaging DBG,
            you ("Client," "you") agree to these Terms.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            1.1 Order of Precedence
          </h3>
          <p style={{ marginBottom: 16 }}>
            DBG engagements may be governed by several documents. Where they
            conflict, the following order controls, from highest to lowest
            authority:
          </p>
          <PrecedenceTable />
          <p style={{ marginTop: 16 }}>
            Where you have a signed MSA, that MSA and its SOWs control for
            matters they address. These Terms fill gaps, govern website and
            self-serve use, and apply in full to engagements where no separate
            signed agreement exists.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              margin: "24px 0 14px",
            }}>
            1.2 License Hierarchy
          </h3>
          <Callout label="If an Executed MSA Exists, the MSA Controls">
            <p>
              Where you and DBG have a signed Master Services Agreement, that
              MSA — together with its Statements of Work, Order forms, and
              License Schedules — governs the engagement for every matter it
              addresses, including intellectual property allocation,
              liability, indemnification, and termination. These Terms apply
              only to fill gaps the MSA does not address, to govern website
              and self-serve use outside any MSA, and to engagements where no
              MSA has been signed. No provision of these Terms narrows,
              overrides, or substitutes for a right or obligation set out in a
              signed MSA.
            </p>
          </Callout>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              margin: "24px 0 14px",
            }}>
            1.3 Authority
          </h3>
          <p>
            If you accept these Terms on behalf of an organization, you
            represent that you are authorized to bind that organization.
            "Client" then refers to that organization.
          </p>
        </Article>

        <Article number="2" title="Key Definitions">
          <DefinitionsTable />
        </Article>

        <Article number="3" title="Accounts & Acceptable Use">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              You are responsible for the accuracy of account information, for
              safeguarding credentials, and for all activity under your accounts
              and those of your authorized users.
            </li>
            <li>
              You will not misuse the Services, attempt to gain unauthorized
              access, interfere with operation, or use the Services in violation
              of applicable law or these Terms.
            </li>
            <li>
              You will not copy, reverse engineer, decompile, scrape, or attempt
              to derive the source code or architecture of the Platform except
              to the limited extent expressly permitted in a signed agreement
              and applicable law.
            </li>
          </ul>
        </Article>

        <Article number="4" title="Fees, Billing & Payment">
          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.1 Right to Invoice and Present Amounts Due at Any Time
          </h3>
          <p style={{ marginBottom: 20 }}>
            DBG may invoice, present a statement, or request payment for any
            fees, recurring charges, usage, overages, expenses, or amounts owed
            at any time, including in advance of, during, or after the
            performance of Services. Issuance of an invoice does not waive any
            other amount owed. Unless an Order states otherwise, fees are quoted
            exclusive of taxes and are non-refundable once the corresponding
            Services or access have been delivered or made available.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.2 Subscriptions & Recurring Plans
          </h3>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 20,
            }}>
            <li>
              Subscriptions begin on the start date in the applicable Order and
              continue for the stated term. Unless the Order says otherwise,
              Subscriptions renew automatically for successive periods of equal
              length until cancelled in accordance with these Terms or the
              Order.
            </li>
            <li>
              Recurring fees are billed in advance of each cycle. Your account
              status reflects payment standing — for example, active, past due,
              or cancelled — and DBG may adjust available features and access
              according to that status.
            </li>
            <li>
              Cancellation takes effect at the end of the then-current paid term
              unless an Order provides otherwise. Fees already billed for a
              current term are not refunded on cancellation except where
              required by law.
            </li>
          </ul>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.3 Late Charges & Managed-Service Late Fees
          </h3>
          <p style={{ marginBottom: 20 }}>
            Undisputed amounts not paid by their due date are past due. Past-due
            amounts accrue a late charge of one and one-half percent (1.5%) per
            month, or the maximum rate permitted by law if lower, from the
            original due date until paid. Where a Managed Service, hosting
            arrangement, uptime commitment, or other availability-dependent
            Service relies on continued payment, DBG may apply late charges and
            reserves all rights in Section 4.4 and Section 5. DBG may also
            recover reasonable costs of collection, including attorneys' fees.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.4 Suspension for Non-Payment
          </h3>
          <p style={{ marginBottom: 20 }}>
            If an account becomes past due, DBG may, after providing notice as
            described below, suspend, throttle, or limit access to the Services,
            the Platform, hosting, support, or deliverables until all past-due
            amounts are paid. DBG will use commercially reasonable efforts to
            provide ten (10) days' prior written notice before suspension,
            except where a shorter period is necessary to protect DBG's systems,
            security, or legal interests. Suspension does not relieve you of the
            obligation to pay amounts owed, and amounts continue to accrue
            during suspension.
          </p>

          <div
            style={{
              border: `1px solid ${PALETTE.borderBright}`,
              background: "rgba(127,161,194,0.05)",
              padding: "18px 20px",
              marginBottom: 20,
            }}>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: PALETTE.teal400,
                marginBottom: 10,
              }}>
              4.5 No Responsibility for Outcomes Caused by Your Non-Payment
            </p>
            <p>
              Where software, a Managed Service, hosting, integration, or any
              other availability-dependent Service is suspended, degraded,
              terminated, or otherwise interrupted as a result of your failure
              to pay amounts when due, DBG is{" "}
              <strong style={{ color: PALETTE.white }}>
                not responsible or financially liable
              </strong>{" "}
              for any resulting loss, downtime, data unavailability, business
              interruption, missed transactions, regulatory exposure, or
              third-party consequence. This applies even where the affected
              software or service is connected to, depends upon, or is relied
              upon by a system you treat as business-critical. You are solely
              responsible for maintaining your own continuity, backups,
              redundancy, and contingency arrangements, and for ensuring that no
              critical operation depends on a DBG Service whose continuity is
              contingent on payment you have not made. Continuity-critical
              dependencies should be addressed in a separate written
              availability or escrow arrangement.
            </p>
          </div>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.6 Taxes
          </h3>
          <p style={{ marginBottom: 20 }}>
            Fees are exclusive of sales, use, VAT, GST, and similar taxes. You
            are responsible for all such taxes other than taxes on DBG's net
            income.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.7 Changes to Pricing
          </h3>
          <p style={{ marginBottom: 20 }}>
            DBG may change recurring fees, plan structures, or rates upon thirty
            (30) days notice, effective at your next renewal. Continued use
            after the effective date constitutes acceptance. Project fees fixed
            in an executed Order remain fixed unless modified by a change order.
          </p>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            4.8 Disputed Amounts
          </h3>
          <p>
            To dispute a charge, you must notify DBG in writing within fifteen
            (15) days of the invoice date, identifying the specific amount and
            basis. Undisputed portions remain due. Amounts not disputed within
            that window are deemed accepted.
          </p>
        </Article>

        <Article
          number="5"
          title="Right to Refuse, Suspend & Terminate Service">
          <p style={{ marginBottom: 16 }}>
            DBG reserves the right, in its reasonable discretion, to decline to
            begin, to suspend, or to terminate any Service, account, or
            engagement, including where:
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 20,
            }}>
            <li>
              amounts are past due, or a payment method is invalid, reversed, or
              subject to chargeback;
            </li>
            <li>
              continuing would create legal, regulatory, security, reputational,
              or safety risk for DBG or others;
            </li>
            <li>
              the engagement falls outside DBG's capabilities, conflicts with
              another client, or requires expertise or licensure DBG does not
              hold;
            </li>
            <li>
              you breach these Terms, an MSA, an Order, or applicable law, or
              misrepresent material facts; or
            </li>
            <li>the working relationship has materially broken down.</li>
          </ul>
          <p>
            Where practicable, DBG will provide notice and, for breaches capable
            of cure, a reasonable opportunity to cure. Termination of a specific
            Service does not affect amounts already owed or provisions intended
            to survive.
          </p>
        </Article>

        <Article number="6" title="Service Suspension Doctrine">
          <p style={{ marginBottom: 16 }}>
            This Section consolidates DBG's suspension rights under Sections
            4.4 and 5 into a single doctrine. DBG may suspend, throttle, or
            limit access to the Services for any of the following reasons,
            individually or in combination:
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 20,
            }}>
            <li>
              <strong style={{ color: PALETTE.white }}>Non-payment.</strong>{" "}
              Past-due amounts, an invalid or reversed payment method, or a
              chargeback, as described in Section 4.4.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>Security.</strong> A
              suspected or actual security incident, vulnerability,
              unauthorized access, or threat to DBG's systems, the Platform,
              or other clients' data.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Regulatory concerns.
              </strong>{" "}
              A good-faith concern that continuing to provide the Services
              would violate, or expose DBG or you to liability under,
              applicable law, regulation, or a regulatory inquiry — including
              healthcare, financial, or data-protection regulation.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Legal or contractual risk.
              </strong>{" "}
              Any other ground identified in Section 5.
            </li>
          </ul>
          <Callout label="Suspension Is a Right, Not a Breach">
            <p>
              A suspension exercised in good faith under this Section or
              Sections 4.4 and 5 is a contractual right, not a breach of these
              Terms, an MSA, an Order, or any service-level commitment by DBG.
              Suspension does not, by itself, entitle you to terminate for
              DBG's breach, withhold otherwise-undisputed fees, or claim
              damages, except as expressly stated in a signed Order or
              service-level schedule. Amounts continue to accrue during a
              suspension for non-payment. DBG will lift a suspension promptly
              once the triggering condition is resolved.
            </p>
          </Callout>
        </Article>

        <Article number="7" title="Managed Services, Uptime & Availability">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              Any uptime or availability target is stated only in an applicable
              Order or service-level schedule. Absent such a schedule, the
              Services are provided without an availability commitment.
            </li>
            <li>
              Where an availability target applies, your sole remedy for failing
              to meet it is the service credit defined in that schedule, not
              refunds or damages. Service credits are DBG's entire liability and
              your exclusive remedy for availability shortfalls.
            </li>
            <li>
              Scheduled maintenance, emergency maintenance, suspension for
              non-payment, force-majeure events, third-party infrastructure or
              provider failures, and issues arising from Client-controlled
              environments or Client modifications are excluded from any
              availability calculation.
            </li>
            <li>
              DBG's availability and support obligations are limited to systems
              under DBG's direct operational control.
            </li>
          </ul>
        </Article>

        <Article number="8" title="Intellectual Property & Source Code">
          <div
            style={{
              border: `1px solid ${PALETTE.borderBright}`,
              background: "rgba(127,161,194,0.05)",
              padding: "18px 20px",
              marginBottom: 20,
            }}>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: PALETTE.teal400,
                marginBottom: 10,
              }}>
              Source Code &amp; IP — No Transfer Without Written Approval and
              Payment in Full
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>7.1</strong> DBG is and
              remains the sole and exclusive owner of the Platform and all of
              its source code, architecture, reusable systems, methodologies,
              tooling, and improvements. Access to or use of the Services does
              not transfer ownership of any of the foregoing.
            </p>
            <p>
              <strong style={{ color: PALETTE.white }}>7.2</strong> You acquire
              no right, title, license, or interest in DBG source code or
              intellectual property beyond the limited rights expressly granted
              in a signed agreement. You may not acquire, take possession of,
              copy, fork, migrate, sublicense, encumber, repackage, or otherwise
              act upon DBG source code or IP — and no source-code delivery or
              ownership transfer occurs — except pursuant to a separate written
              agreement signed by an authorized DBG executive and{" "}
              <strong style={{ color: PALETTE.white }}>
                only upon payment in full of the corresponding fee.
              </strong>{" "}
              Until both conditions are met, any purported transfer, assignment,
              or claim of right is void.
            </p>
          </div>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              Where source code is delivered under a signed Order or License
              Schedule, your rights are exactly those defined there, and DBG
              retains ownership of the underlying Platform regardless of the
              breadth of the license.
            </li>
            <li>
              You retain ownership of Client Content. You grant DBG a limited
              license to host, process, and use Client Content solely to provide
              the Services, as further described in the Privacy Policy and any
              DPA or BAA.
            </li>
            <li>
              If you provide feedback or suggestions, DBG may use them without
              restriction or obligation.
            </li>
          </ul>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              margin: "24px 0 14px",
            }}>
            8.3 Technology Transfer Boundary
          </h3>
          <Callout label="Technology Transfer Expands Rights, Not Ownership">
            <p style={{ marginBottom: 12 }}>
              This is DBG's governing doctrine for every technology-transfer,
              source-delivery, and licensing engagement: a transfer of
              technology expands what you can do with the Platform; it never
              changes what the Platform is, or who owns it.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                Source delivery ≠ ownership.
              </strong>{" "}
              Receiving source code, build artifacts, repositories, or
              deployment access is a delivery of materials under license. It
              is not, and does not imply, a transfer of ownership in the
              Platform or any Generic Improvement.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                License rights ≠ ownership.
              </strong>{" "}
              However broad a license you are granted — including perpetual,
              exclusive-use, or unrestricted-operational-use licenses — a
              license is a right to use, not a transfer of title. DBG retains
              ownership of the underlying Platform and intellectual property
              regardless of license scope.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                Technology transfer ≠ assignment of IP.
              </strong>{" "}
              A technology-transfer engagement, License Schedule, or transfer
              fee does not assign, convey, or encumber DBG's ownership of its
              intellectual property. Assignment of ownership occurs only
              through a separate written agreement that expressly states an
              assignment, signed by an authorized DBG executive, and only as
              to the specific, named deliverable it identifies.
            </p>
            <p>
              Where a signed MSA, SOW, or License Schedule defines a
              different allocation for a specific deliverable, that document
              controls for that deliverable; this doctrine governs everywhere
              else.
            </p>
          </Callout>
        </Article>

        <Article number="9" title="Client Data & Privacy">
          <p>
            DBG's handling of data is described in the Privacy Policy,
            incorporated by reference. Where a signed MSA, SOW,
            technology-transfer agreement, DPA, or BAA addresses ownership or
            use of specific data, that agreement controls for that data. You are
            responsible for ensuring you have the rights and consents necessary
            for DBG to process the data you submit.
          </p>
        </Article>

        <Article number="10" title="Recordings">
          <p style={{ marginBottom: 16 }}>DBG may record and retain:</p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 20,
            }}>
            <li>discovery calls and initial consultations;</li>
            <li>training sessions and onboarding walkthroughs;</li>
            <li>
              project meetings, status calls, video conferences, and
              screen-shares;
            </li>
            <li>product demonstrations and demo recordings;</li>
            <li>
              meeting transcripts, chat and messaging threads, and support
              interactions; and
            </li>
            <li>
              audio and visual material generated during onboarding, delivery,
              and support.
            </li>
          </ul>

          <p style={{ marginBottom: 16 }}>
            <strong style={{ color: PALETTE.white }}>9.1 Consent.</strong> By
            participating in such communications, you and your representatives
            consent to being recorded and to DBG's storage and use of the
            recordings for the purposes above. Where a session will be recorded,
            DBG will provide notice at or before the start of the session, and
            any participant who does not consent should not proceed with the
            recorded session.
          </p>
          <p style={{ marginBottom: 20 }}>
            <strong style={{ color: PALETTE.white }}>
              9.2 Ownership & Use.
            </strong>{" "}
            Recordings made by DBG are DBG materials. DBG will handle them in
            accordance with the Privacy Policy and, where they contain regulated
            data, the applicable BAA or DPA. DBG will not publish recordings
            that identify you externally without your consent, except as
            required by law.
          </p>

          <div
            style={{
              border: `1px solid ${PALETTE.borderBright}`,
              background: "rgba(127,161,194,0.05)",
              padding: "18px 20px",
            }}>
            <p
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: PALETTE.teal400,
                marginBottom: 10,
              }}>
              Consent-to-Record — Jurisdiction Note
            </p>
            <p>
              Several states require the consent of all parties to a
              conversation before it may be recorded. The notice-and-consent
              mechanism described above is intended to satisfy all-party-consent
              requirements in every U.S. jurisdiction, regardless of where
              Company is headquartered or otherwise conducts business. This
              obligation is determined by the location of each party to the
              communication at the time it occurs, not by where Company
              operates, and applies whenever any party is located in an
              all-party-consent jurisdiction, including but not limited to
              California, Connecticut, Delaware, Florida, Illinois, Maryland,
              Massachusetts, Montana, Nevada, New Hampshire, Oregon,
              Pennsylvania, and Washington. The exact wording of the notice, the
              timing of disclosure, and the handling of recordings or
              transcripts that capture PHI or other regulated data must be
              confirmed with counsel for each jurisdiction in which a party to
              the communication is located. For communications involving parties
              located outside the United States, additional consent and
              data-protection requirements may apply — for example, under the
              EU/UK GDPR, Canada's PIPEDA, or other applicable foreign law — and
              such requirements must likewise be confirmed with counsel before
              recording.
            </p>
          </div>
        </Article>

        <Article number="11" title="Compliance & Regulated Engagements">
          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            11.1 Healthcare & HIPAA
          </h3>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 20,
            }}>
            <li>
              You must not transmit PHI to DBG systems before a Business
              Associate Agreement is executed. Where DBG acts as a Business
              Associate, the BAA governs PHI handling and controls over these
              Terms for that subject matter.
            </li>
            <li>
              HIPAA compliance is a shared operational responsibility that
              depends on administrative, physical, and technical safeguards. DBG
              does not represent that use of the Services alone makes you
              HIPAA-compliant, and DBG does not claim to be "HIPAA certified."
              You remain responsible for your workforce training, policies,
              patient authorizations, and safeguards in environments under your
              control.
            </li>
          </ul>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            11.2 Artificial Intelligence & Automated Outputs
          </h3>
          <Callout label="AI Outputs Require Human Review — Not a Substitute for Professional Judgment">
            <p style={{ marginBottom: 12 }}>
              Where the Services include AI-assisted features, automation,
              generative tooling, or other AI Outputs, the following apply in
              addition to Section 12 (Warranties &amp; Disclaimers):
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                AI Outputs may be inaccurate.
              </strong>{" "}
              AI Outputs are generated probabilistically and may be
              incomplete, outdated, biased, or factually incorrect. DBG does
              not warrant the accuracy, completeness, or reliability of any AI
              Output.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                Human review is required.
              </strong>{" "}
              You are responsible for reviewing, validating, and testing any
              AI Output before relying on it, deploying it, acting on it, or
              providing it to a third party, especially in automated
              workflows.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                No guarantee of results.
              </strong>{" "}
              DBG does not guarantee any business, operational, financial,
              marketing, or other outcome from the use of AI-assisted features
              or automation.
            </p>
            <p style={{ marginBottom: 12 }}>
              <strong style={{ color: PALETTE.white }}>
                Not professional advice.
              </strong>{" "}
              AI Outputs do not constitute legal, tax, accounting, financial,
              clinical, or other licensed professional advice and must not be
              relied upon as such.
            </p>
            <p>
              <strong style={{ color: PALETTE.white }}>
                No medical advice.
              </strong>{" "}
              AI Outputs must never be used or relied upon as medical advice,
              a clinical recommendation, or a substitute for the judgment of a
              qualified healthcare professional. This restriction is
              particularly important for healthcare-related and automation
              Services and applies regardless of how an AI Output is phrased
              or presented.
            </p>
          </Callout>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            11.3 Security Program & SOC 2
          </h3>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginBottom: 20,
            }}>
            <li>
              DBG maintains a security program with administrative, technical,
              and physical safeguards appropriate to the Services. The current
              scope of that program, and the status of any third-party
              attestations or reports, is described in DBG's then-current
              security documentation, available on request.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                No misstatement of attestation status.
              </strong>{" "}
              SOC 2 and similar frameworks are attestations performed by
              third-party auditors, not certifications. DBG will describe its
              SOC 2 or other attestation status truthfully and will not
              represent that it holds an attestation it has not received. Where
              you require a specific attestation, report, or control set,
              address it in your Order.
            </li>
          </ul>

          <h3
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15.5px",
              color: PALETTE.white,
              marginBottom: 14,
            }}>
            11.4 Tax, Financial, Legal & Other Professional Matters
          </h3>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              DBG provides technology and related services. DBG does not provide
              legal, tax, accounting, financial, clinical, or other licensed
              professional advice, and nothing in the Services constitutes such
              advice. You are responsible for engaging qualified professionals
              and for your own compliance.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>
                Regulatory change.
              </strong>{" "}
              DBG is not responsible for monitoring, updating, or modifying
              delivered software, infrastructure, or documentation to
              accommodate future legal, regulatory, tax, billing, reimbursement,
              healthcare, or industry-standard changes unless expressly
              contracted to do so. You are responsible for keeping your deployed
              environment compliant with applicable law.
            </li>
          </ul>
        </Article>

        <Article number="12" title="Warranties & Disclaimers">
          <p
            style={{
              textTransform: "uppercase",
              fontSize: "13.5px",
              letterSpacing: "0.01em",
              color: PALETTE.whiteSoft,
            }}>
            Except as expressly stated in a signed Order, the Services,
            Platform, and all deliverables are provided "as is" and "as
            available" without warranty of any kind, express or implied. DBG
            disclaims all implied warranties of merchantability, fitness for a
            particular purpose, title, and non-infringement, and does not
            warrant uninterrupted or error-free operation, specific business,
            financial, or clinical outcomes, regulatory compliance, the
            accuracy of any AI Output, or the
            absence of security vulnerabilities.
          </p>
        </Article>

        <Article number="13" title="Limitation of Liability">
          <p
            style={{
              textTransform: "uppercase",
              fontSize: "13.5px",
              letterSpacing: "0.01em",
              color: PALETTE.whiteSoft,
              marginBottom: 16,
            }}>
            To the maximum extent permitted by law, neither party is liable for
            any indirect, incidental, special, consequential, punitive, or
            exemplary damages, or for lost profits, revenue, data, or business,
            arising out of or related to the Services, even if advised of the
            possibility.
          </p>
          <p
            style={{
              textTransform: "uppercase",
              fontSize: "13.5px",
              letterSpacing: "0.01em",
              color: PALETTE.whiteSoft,
            }}>
            DBG's total cumulative liability arising out of or related to the
            Services shall not exceed the greater of the fees paid to DBG for
            the applicable Service in the twelve 12 months preceding the claim.
            These limits do not apply to a party's gross negligence or willful
            misconduct, your indemnification obligations, or breach of DBG's
            intellectual-property or confidentiality rights.
          </p>
        </Article>

        <Article number="14" title="Indemnification">
          <p>
            You will defend, indemnify, and hold harmless DBG and its officers,
            employees, and contractors from claims, damages, and costs
            (including reasonable attorneys' fees) arising from: your violation
            of law or these Terms; your Client Content or its infringement of
            third-party rights; your use of the Services outside the scope of a
            signed agreement; modifications you make to delivered software; your
            data, security, or regulatory failures in environments under your
            control; your reliance on an AI Output without the review required
            by Section 11.2; and your marketing, sales, or distribution
            activities.
          </p>
        </Article>

        <Article number="15" title="Confidentiality">
          <p>
            Each party will protect the other's non-public information with at
            least reasonable care and use it only to perform or receive the
            Services. DBG source code, architecture, methodologies, and pricing
            are DBG Confidential Information. Where a signed MSA contains
            confidentiality terms, those terms control.
          </p>
        </Article>

        <Article number="16" title="Case Study & Marketing Rights">
          <p style={{ marginBottom: 16 }}>
            Subject to the confidentiality obligations in Section 15 and the
            opt-out right below, you grant DBG the right to:
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
              marginBottom: 20,
            }}>
            <li>
              reference your company name and the nature of the engagement in
              DBG's portfolio, proposals, and new-business materials;
            </li>
            <li>
              describe the engagement in case studies, testimonials, and
              marketing copy, in general terms that do not disclose your
              Confidential Information or Client Content; and
            </li>
            <li>
              display your logo on DBG's website and in sales materials as a
              representative client.
            </li>
          </ul>
          <p>
            These rights do not extend to confidential deal terms, pricing,
            non-public roadmaps, or Client Content, and do not apply to any
            engagement subject to a confidential launch, private campaign, or
            restricted-brand agreement under Section 15. You may opt out of
            some or all of these uses, or request removal of previously
            published material, at any time by written notice to DBG; DBG
            will implement the opt-out within a commercially reasonable time
            and will not initiate new uses inconsistent with it.
          </p>
        </Article>

        <Article number="17" title="Open Source & Third-Party Components">
          <p style={{ marginBottom: 16 }}>
            The Platform and deliverables may incorporate or rely on
            open-source and other third-party libraries, frameworks, and
            dependencies ("Open Source Components"). The following apply
            regardless of any other provision of these Terms:
          </p>
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}>
            <li>
              Open Source Components remain owned by their respective
              licensors and are governed by their own license terms, which
              apply alongside these Terms;
            </li>
            <li>
              DBG's use or inclusion of an Open Source Component does not
              transfer, assign, or create any ownership interest in that
              component for DBG or for you, and does not expand DBG's
              ownership claim over the Platform beyond DBG's own original
              work and any rights DBG separately holds;
            </li>
            <li>
              you are responsible for your own compliance with the license
              terms of any Open Source Component you continue to use, modify,
              or redistribute following a technology transfer; and
            </li>
            <li>
              DBG does not warrant that any Open Source Component is free of
              defects, security vulnerabilities, or third-party claims, and
              disclaims liability for Open Source Components to the same
              extent as set out in Sections 12 and 13.
            </li>
          </ul>
        </Article>

        <Article number="18" title="Beta Features & Experimental Functionality">
          <Callout label="Beta Program — Provided As-Is, Subject to Change or Withdrawal">
            <p style={{ marginBottom: 12 }}>
              From time to time, DBG may make AI features, preview releases,
              or other experimental or pre-general-availability functionality
              ("Beta Features") available to you for evaluation. Beta
              Features are optional and, unless an Order says otherwise:
            </p>
            <ul
              style={{
                paddingLeft: 20,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginBottom: 12,
              }}>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  provided "as is"
                </strong>{" "}
                without any warranty, and without the service-level or
                support commitments that apply to generally available
                Services;
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  may change, be limited, or be discontinued
                </strong>{" "}
                at any time without notice and without liability to you;
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  are excluded from any uptime, availability, or
                  service-credit schedule
                </strong>{" "}
                under Section 7; and
              </li>
              <li>
                <strong style={{ color: PALETTE.white }}>
                  may be subject to additional or different terms
                </strong>{" "}
                communicated at the time the Beta Feature is made available,
                which control for that Beta Feature.
              </li>
            </ul>
            <p>
              Feedback you provide on a Beta Feature is governed by Section 8
              (Intellectual Property & Source Code).
            </p>
          </Callout>
        </Article>

        <Article number="19" title="Term, Suspension & Termination Effects">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              These Terms apply for as long as you access the Services.
              Individual engagements run for the term in their Order.
            </li>
            <li>
              On termination: access to DBG-hosted components ends; all
              outstanding undisputed amounts become immediately due; you may
              export Client Content from DBG-managed systems within thirty 30
              days; and DBG retains all Platform and IP rights.
            </li>
            <li>
              Sections addressing payment, intellectual property,
              confidentiality, disclaimers, limitation of liability,
              indemnification, and dispute resolution survive termination.
            </li>
          </ul>
        </Article>

        <Article number="20" title="Dispute Resolution & Governing Law">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              The parties will first attempt to resolve disputes through
              good-faith negotiation, then non-binding mediation.
            </li>
            <li>
              Unresolved disputes will be settled by binding arbitration under
              the Commercial Arbitration Rules of the American Arbitration
              Association before a single arbitrator, seated in Detroit,
              Michigan. Either party may seek emergency or injunctive relief
              from a court to protect intellectual property or confidential
              information.
            </li>
            <li>
              The prevailing party may recover reasonable attorneys' fees and
              costs. Disputes are resolved individually; class and consolidated
              proceedings are waived.
            </li>
            <li>
              These Terms are governed by the laws of the State of Michigan,
              without regard to conflict-of-law rules.
            </li>
          </ul>
        </Article>

        <Article number="21" title="Our Approach to the Relationship">
          <p>
            Everything above exists to make the engagement predictable for
            both sides — it is not how we hope to spend most of our time
            together. In practice, that means clear scope before work starts,
            transparent invoicing, responsive support during delivery, and a
            straightforward conversation any time something needs to change.
            If a Service isn't working the way you expected, tell us; most
            issues are resolved faster through a direct conversation with
            your delivery team than through any clause in this document.
            Where these Terms set a floor, our aim in every engagement is to
            do better than the floor.
          </p>
        </Article>

        <Article number="22" title="Changes to These Terms">
          <p>
            DBG may update these Terms from time to time. Material changes will
            be indicated by updating the "Last Updated" date and, where
            appropriate, by notice. Continued use after the effective date
            constitutes acceptance. The version in effect when an engagement
            begins governs that engagement unless updated by agreement.
          </p>
        </Article>

        <Article number="23" title="General">
          <ul
            style={{
              paddingLeft: 20,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}>
            <li>
              <strong style={{ color: PALETTE.white }}>Assignment:</strong> you
              may not assign these Terms without DBG's consent; DBG may assign
              in connection with a merger, acquisition, or sale of assets.
            </li>
            <li>
              <strong style={{ color: PALETTE.white }}>Force majeure:</strong>{" "}
              neither party is liable for delays beyond its reasonable control;
              this does not excuse payment obligations.
            </li>
            <li>
              Notices are in writing by email with confirmation, certified mail,
              or courier. Severability and no-waiver apply. Electronic
              signatures and acceptance are valid. These Terms, with any signed
              agreements and Orders, are the entire agreement.
            </li>
          </ul>
        </Article>

        <div
          style={{
            marginTop: 64,
            paddingTop: 32,
            borderTop: `1px solid ${PALETTE.border}`,
          }}>
          <p
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "10px",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: PALETTE.teal400,
              marginBottom: 14,
            }}>
            ✉ Contact
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontWeight: 300,
              fontSize: "15px",
              lineHeight: 1.8,
              color: PALETTE.whiteMid,
            }}>
            Digital Billions Group LLC &nbsp;·&nbsp;{" "}
            <Placeholder>legal@digitalbillionsgroup.com</Placeholder>
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
